import { Component, Structure } from '@wix/thunderbolt-becky-types'
import * as _ from 'lodash'
import { PINNED_LAYER_SUFFIX } from '../constants'
import {
	PinnedPositions,
	PinnedChildrenByPositionMap,
	checkIfPinnedComponentAndAddToMap,
	createComponent,
	isFixed,
} from './utils/pinnedComponentsUtils'

const GROUP_TYPES = {
	LEGACY_GAPS: 'LEGACY_GAPS',
	PAGES_CONTAINER: 'PAGES_CONTAINER',
	PINNED: 'PINNED',
	SOAP: 'SOAP',
	STRUCTURAL: 'STRUCTURAL',
} as const

const PINNED_GROUPS_IDS: Record<PinnedPositions, string> = {
	TOP_LEFT: 'pinnedTopLeft',
	TOP_CENTER: 'pinnedTopCenter',
	TOP_RIGHT: 'pinnedTopRight',
	CENTER_LEFT: 'pinnedMiddleLeft',
	CENTER_RIGHT: 'pinnedMiddleRight',
	BOTTOM_LEFT: 'pinnedBottomLeft',
	BOTTOM_CENTER: 'pinnedBottomCenter',
	BOTTOM_RIGHT: 'pinnedBottomRight',
}

export type MasterPageGroup = {
	type: keyof typeof GROUP_TYPES
	components: Array<string>
	id?: string
	parent?: string
}

type ComponentsByType = {
	pinned: PinnedChildrenByPositionMap
	soap: Record<string, Array<string>>
	structural: Array<string>
}

const isHeaderOrFooter = (compId: string) => compId === 'SITE_HEADER' || compId === 'SITE_FOOTER'
const shouldAddPlaceholder = (compId: string, layout: Component['layout']) =>
	isHeaderOrFooter(compId) && isFixed(layout)
const getHeaderOrFooterWrapperId = (compId: string) => `${compId}_WRAPPER`

const createPinnedGroup = (components: Array<string>, groupId: string, parent: string): MasterPageGroup => ({
	type: GROUP_TYPES.PINNED,
	components,
	id: groupId,
	parent,
})
const createSOAPGroup = (components: Array<string>, groupId: string): MasterPageGroup => ({
	type: GROUP_TYPES.SOAP,
	components,
	id: groupId,
})
const createStructuralGroup = (compId: string): MasterPageGroup => ({
	type: GROUP_TYPES.STRUCTURAL,
	components: [compId],
})

const getIsStructural = (isMobileView: boolean) => {
	const structuralIDs: { [compId: string]: boolean } = isMobileView
		? {
				SITE_HEADER: true,
				SITE_FOOTER: true,
				PAGES_CONTAINER: true,
				BACK_TO_TOP_BUTTON: true,
				SOSP_CONTAINER_CUSTOM_ID: true,
				QUICK_ACTION_BAR: true,
		  }
		: { SITE_HEADER: true, SITE_FOOTER: true, PAGES_CONTAINER: true, BACK_TO_TOP_BUTTON: true }
	return (id: string) => Boolean(structuralIDs![id])
}

const getMasterPageChildrenGroupsForMesh = (
	childrenIds: Array<string>,
	structure: Structure,
	isMobileView: boolean
) => {
	const isStructural = getIsStructural(isMobileView)
	return childrenIds.map((childrenId) => {
		if (isStructural(childrenId)) {
			return {
				type: 'STRUCTURAL',
				components: [childrenId],
			}
		}
		return {
			type: 'SOAP',
			components: structure[childrenId].components?.map((compId) => structure[compId]),
			id: childrenId,
		}
	})
}

const getMasterPageChildrenGroups = (
	childrenIds: Array<string>,
	childrenLayout: { [compId: string]: Component['layout'] },
	isMobileView: boolean
): Array<MasterPageGroup> => {
	const isStructural = getIsStructural(isMobileView)
	const pagesContainerIdx = childrenIds.findIndex((childId) => childId === 'PAGES_CONTAINER')

	const componentsByType = childrenIds.reduce(
		(acc, childId, idx) => {
			if (isStructural(childId)) {
				acc.structural.push(childId)
			} else {
				const childLayout = childrenLayout[childId]
				const isPinned = checkIfPinnedComponentAndAddToMap(childId, childLayout, acc.pinned)
				if (!isPinned && !isFixed(childLayout)) {
					acc.soap[idx < pagesContainerIdx ? 'beforeMain' : 'afterMain'].push(childId)
				}
			}

			return acc
		},
		{
			pinned: {},
			soap: {
				beforeMain: [],
				afterMain: [],
			},
			structural: [],
		} as ComponentsByType
	)

	const createPinnedGroupIfNotEmpty = (position: PinnedPositions, parent: 'SITE_HEADER' | 'SITE_FOOTER') =>
		componentsByType.pinned[position] &&
		createPinnedGroup(
			componentsByType.pinned[position]!,
			PINNED_GROUPS_IDS[position],
			getHeaderOrFooterWrapperId(parent)
		)

	return [
		createPinnedGroupIfNotEmpty('TOP_LEFT', 'SITE_HEADER'),
		createPinnedGroupIfNotEmpty('TOP_CENTER', 'SITE_HEADER'),
		createPinnedGroupIfNotEmpty('TOP_RIGHT', 'SITE_HEADER'),
		componentsByType.structural.includes('SITE_HEADER') && createStructuralGroup('SITE_HEADER'),
		componentsByType.structural.includes('SOSP_CONTAINER_CUSTOM_ID') &&
			createStructuralGroup('SOSP_CONTAINER_CUSTOM_ID'),
		createPinnedGroupIfNotEmpty('CENTER_LEFT', 'SITE_HEADER'),
		createPinnedGroupIfNotEmpty('CENTER_RIGHT', 'SITE_HEADER'),
		componentsByType.soap.beforeMain.length > 0 &&
			createSOAPGroup(componentsByType.soap.beforeMain, 'soapBeforePagesContainer'),
		componentsByType.structural.includes('PAGES_CONTAINER') && createStructuralGroup('PAGES_CONTAINER'),
		componentsByType.soap.afterMain.length > 0 &&
			createSOAPGroup(componentsByType.soap.afterMain, 'soapAfterPagesContainer'),
		createPinnedGroupIfNotEmpty('BOTTOM_LEFT', 'SITE_FOOTER'),
		createPinnedGroupIfNotEmpty('BOTTOM_CENTER', 'SITE_FOOTER'),
		createPinnedGroupIfNotEmpty('BOTTOM_RIGHT', 'SITE_FOOTER'),
		componentsByType.structural.includes('SITE_FOOTER') && createStructuralGroup('SITE_FOOTER'),
		componentsByType.structural.includes('BACK_TO_TOP_BUTTON') && createStructuralGroup('BACK_TO_TOP_BUTTON'),
		componentsByType.structural.includes('QUICK_ACTION_BAR') && createStructuralGroup('QUICK_ACTION_BAR'),
	].filter((group) => group) as Array<MasterPageGroup>
}

const addPinnedGroupToStructure = (structureComps: Structure, group: MasterPageGroup) => {
	const components: Array<string> = []
	_.forEach(group.components, (id) => {
		const pinnedId = `${id}${PINNED_LAYER_SUFFIX}`
		structureComps[pinnedId] = createComponent(pinnedId, [id], group.id!, 'PinnedLayer', 'Container')
		components.push(pinnedId)
	})

	structureComps[group.id!] = createComponent(group.id!, components, group.parent!, 'DivWithChildren')
	structureComps[group.parent!].components!.push(group.id!)
}

const addGroupedCompsToStructure = (
	structureComps: Structure,
	masterPageChildrenGroups: Array<MasterPageGroup>,
	childrenLayout: { [compId: string]: Component['layout'] }
) => {
	_.forEach(masterPageChildrenGroups, (group) => {
		if (group.id) {
			// nonStructuralGroup
			if (group.type === GROUP_TYPES.PINNED) {
				addPinnedGroupToStructure(structureComps, group)
			} else {
				structureComps[group.id] = createComponent(
					group.id,
					group.components,
					'masterPage',
					'MeshGroup',
					'Container'
				)
			}
		} else {
			const compId = group.components[0]
			if (isHeaderOrFooter(compId)) {
				const wrapperId = getHeaderOrFooterWrapperId(compId)
				structureComps[wrapperId].components!.push(compId)
			}
			if (shouldAddPlaceholder(compId, childrenLayout[compId])) {
				const placeholderId = `${compId}-placeholder`
				structureComps[placeholderId] = {
					id: placeholderId,
					componentType: 'DivWithChildren',
					layout: { height: childrenLayout[compId].height },
					type: 'Component',
					parent: 'masterPage',
				} as Component
			}
		}
	})

	return structureComps
}

const createHeaderAndFooterWrappers = () => {
	const headerWrapperId = getHeaderOrFooterWrapperId('SITE_HEADER')
	const footerWrapperId = getHeaderOrFooterWrapperId('SITE_FOOTER')

	return {
		[headerWrapperId]: createComponent(headerWrapperId, [], 'masterPage', 'ContainerWrapper'),
		[footerWrapperId]: createComponent(footerWrapperId, [], 'masterPage', 'ContainerWrapper'),
	}
}

const convertMasterPageGroupsToStructure = (
	masterPage: Structure['masterPage'],
	masterPageGroups: Array<MasterPageGroup>,
	childrenLayout: { [compId: string]: Component['layout'] }
): Structure => {
	const masterPageChildrenGroups = _.flatMap(masterPageGroups, (childrenGroup) => {
		const compId = childrenGroup.components[0]
		if (shouldAddPlaceholder(compId, childrenLayout[compId])) {
			return {
				...childrenGroup,
				components: childrenGroup.components.concat(`${compId}-placeholder`),
			}
		}
		return childrenGroup
	})

	const nonPinnedMasterPageChildrenGroups = _.filter(
		masterPageChildrenGroups,
		(group) => group.type !== GROUP_TYPES.PINNED
	)
	const masterPageChildren = _.flatMap(nonPinnedMasterPageChildrenGroups, (group) => {
		if (group.type === GROUP_TYPES.SOAP) {
			return group.id
		} else {
			const components = [...group.components]
			return _.map(components, (compId) => {
				if (isHeaderOrFooter(compId)) {
					return getHeaderOrFooterWrapperId(compId)
				}
				return compId
			})
		}
	})

	const structureComps = {
		masterPage: { ...masterPage, components: masterPageChildren },
		...createHeaderAndFooterWrappers(),
	} as Structure
	return addGroupedCompsToStructure(structureComps, masterPageChildrenGroups, childrenLayout)
}
export {
	GROUP_TYPES,
	getMasterPageChildrenGroups,
	convertMasterPageGroupsToStructure,
	getMasterPageChildrenGroupsForMesh,
}
